/**
 * 訂單明細 Order Lines lens (S4.3).
 *
 * The projected FulfillmentLine grid: Internal Product ID + SKU identity,
 * `QuantityBucketBar` (訂購/已確認/已裝/已收 with 待出量 as the unfilled
 * remainder, per-segment numbers as the non-colour channel), late-bound
 * 進度點 chip, 裝櫃批次 links, owner. Row → `LineDrawer` with the
 * `LinkedDocumentRail` + bucket event timeline.
 *
 * ux-lens finding 5 folded in:
 * - SO/PO 對照 is a GROUPING TOGGLE (Segmented), not a tab;
 * - 待出量 is a saved filter (`balanceOnly`) reached from the stat-card
 *   drill, not a tab.
 * Filtering is client-side over the mock-scale projection (static cacheKey).
 */

import { useMemo, useState } from 'react';
import { Badge, Button, Dropdown, Input, Segmented, Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import QuantityBucketBar from '@/components/common/QuantityBucketBar';
import { useOccDocIndex, useOccExceptions, useOccLines, useOccNodeConfigs } from '@/hooks/useOcc';
import { useOccStore } from '@/stores/occStore';
import type { OccNodeFilter, OccOwnerFilter } from '@/stores/occStore';
import type { OccLensLine } from '@/lib/occ/selectors';
import type { FulfillmentOwnerRole } from '@/types/api/occ';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import {
  countLinesFilterBadges,
  filterOccLines,
  groupLinesBySo,
} from '../occWorkbench';
import type { SoPoGroupRow } from '../occWorkbench';
import { LensFooter, LensUnavailableNotice } from '../lensShared';
import LineDrawer from '../LineDrawer';
import BatchDrawer from '../BatchDrawer';

const OWNER_ROLES: FulfillmentOwnerRole[] = ['procurement', 'shipping', 'finance'];

interface LinesFilterPopoverValues extends FilterValues {
  node?: string;
  owner?: string;
  exceptionsOnly?: string;
  balanceOnly?: string;
}

const OrderLinesLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();

  const [searchParams, setSearchParams] = useSearchParams();
  // Route-derived `?productId=` scope (SR2.2) — the canonical internalProductId
  // the 產品管控中心 quintet deep-links with (SR3.5). Read from the URL, never
  // stored, so the lens lands already scoped and the chip clears by dropping
  // the param.
  const productFilter = searchParams.get('productId');

  const { lines, syncedAt, isLoading, error, refresh, unavailable } = useOccLines();
  const { rows: exceptionRows } = useOccExceptions();
  const { configs, nodeLabel } = useOccNodeConfigs();
  const docIndex = useOccDocIndex();
  const { linesFilters, setLinesFilters, resetLinesFilters } = useOccStore();

  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('productId');
    setSearchParams(next, { replace: true });
  };

  const clearAllFilters = () => {
    resetLinesFilters();
    clearProductFilter();
  };

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  // Record + open are decoupled so the closing animation keeps its content.
  const [drawerLine, setDrawerLine] = useState<OccLensLine | null>(null);
  const [lineDrawerOpen, setLineDrawerOpen] = useState(false);
  const [drawerBatchId, setDrawerBatchId] = useState<string | null>(null);
  const [batchDrawerOpen, setBatchDrawerOpen] = useState(false);

  const openLineDrawer = (line: OccLensLine) => {
    setDrawerLine(line);
    setLineDrawerOpen(true);
  };
  const openBatchDrawer = (batchId: string) => {
    setDrawerBatchId(batchId);
    setBatchDrawerOpen(true);
  };

  const flaggedLineIds = useMemo(
    () => new Set(exceptionRows.map((row) => row.lineId)),
    [exceptionRows]
  );

  const filteredLines = useMemo(
    () => filterOccLines(lines, linesFilters, flaggedLineIds, productFilter),
    [lines, linesFilters, flaggedLineIds, productFilter]
  );

  // Chip label — the scoped product's SKU when a line for it is present, else
  // the raw internalProductId (the deep link may outlive the last open line).
  const productChipLabel = useMemo(
    () => lines.find((line) => line.internalProductId === productFilter)?.sku ?? productFilter,
    [lines, productFilter]
  );

  const groupedRows = useMemo(
    () => (linesFilters.groupBySo ? groupLinesBySo(filteredLines) : []),
    [linesFilters.groupBySo, filteredLines]
  );

  const activeFilterCount = countLinesFilterBadges(linesFilters);
  const isFiltered =
    activeFilterCount > 0 || linesFilters.search.trim().length > 0 || Boolean(productFilter);

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'node',
        label: t('occ:occ.lines.filters.node'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('occ:occ.lines.filters.allNodes') },
          // Late-bound: options come from the config rows, never raw codes.
          ...configs.map((config) => ({ value: config.code, label: nodeLabel(config.code) })),
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'owner',
        label: t('occ:occ.lines.filters.owner'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('occ:occ.lines.filters.allOwners') },
          ...OWNER_ROLES.map((role) => ({ value: role, label: t(`occ:occ.owner.${role}`) })),
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'exceptionsOnly',
        label: t('occ:occ.lines.filters.exceptionsOnly'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('occ:occ.lines.filters.exceptionsAll') },
          { value: 'yes', label: t('occ:occ.lines.filters.exceptionsYes') },
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'balanceOnly',
        label: t('occ:occ.lines.filters.balance'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('occ:occ.lines.filters.balanceAll') },
          { value: 'yes', label: t('occ:occ.lines.filters.balanceOpen') },
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t, configs, nodeLabel]
  );

  const initialPopoverFilters: LinesFilterPopoverValues = useMemo(
    () => ({
      node: linesFilters.node,
      owner: linesFilters.owner,
      exceptionsOnly: linesFilters.exceptionsOnly ? 'yes' : 'all',
      balanceOnly: linesFilters.balanceOnly ? 'yes' : 'all',
    }),
    [linesFilters]
  );

  const handleApplyFilters = (values: LinesFilterPopoverValues) => {
    setLinesFilters({
      node: (values.node as OccNodeFilter) || 'all',
      owner: (values.owner as OccOwnerFilter) || 'all',
      exceptionsOnly: values.exceptionsOnly === 'yes',
      balanceOnly: values.balanceOnly === 'yes',
    });
  };

  const bucketLabels = {
    ordered: t('occ:occ.buckets.ordered'),
    confirmed: t('occ:occ.buckets.confirmed'),
    loaded: t('occ:occ.buckets.loaded'),
    received: t('occ:occ.buckets.received'),
    balance: t('occ:occ.buckets.orderBalance'),
    negativeWarning: t('occ:occ.buckets.negativeWarning'),
  };

  const renderProductCell = (line: OccLensLine) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span style={{ fontWeight: token.fontWeightStrong }}>
        <HighlightText text={line.sku} searchTerm={linesFilters.search} fallback="-" />
      </span>
      <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        <HighlightText
          text={line.internalProductId}
          searchTerm={linesFilters.search}
          fallback=""
        />
      </span>
    </div>
  );

  const renderBatchesCell = (line: OccLensLine) => {
    const batches = docIndex.batchesForLine(line.id);
    if (batches.length === 0) return renderDash();
    return (
      <div className="tw-flex tw-flex-col tw-gap-1">
        {batches.map((batch) => (
          <Tag
            key={batch.id}
            style={{ margin: 0, cursor: 'pointer', color: token.colorPrimary }}
            onClick={(event) => {
              event.stopPropagation();
              openBatchDrawer(batch.id);
            }}
          >
            {batch.batchNumber}
          </Tag>
        ))}
      </div>
    );
  };

  const renderExceptionMark = (line: OccLensLine) =>
    flaggedLineIds.has(line.id) ? (
      <WarningOutlined
        aria-label={t('occ:occ.lines.filters.exceptionsYes')}
        style={{ color: token.colorError }}
      />
    ) : null;

  const flatColumns: TableProps<OccLensLine>['columns'] = [
    {
      title: t('occ:occ.lines.columns.product'),
      key: 'product',
      width: 190,
      render: (_: unknown, line) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          {renderProductCell(line)}
          {renderExceptionMark(line)}
        </div>
      ),
    },
    {
      title: t('occ:occ.lines.columns.order'),
      key: 'order',
      width: 200,
      render: (_: unknown, line) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <HighlightText text={line.soNumber} searchTerm={linesFilters.search} fallback="-" />
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            <HighlightText
              text={line.customerName}
              searchTerm={linesFilters.search}
              fallback=""
            />
          </span>
        </div>
      ),
    },
    {
      title: t('occ:occ.lines.columns.buckets'),
      key: 'buckets',
      width: 290,
      render: (_: unknown, line) => (
        <QuantityBucketBar
          qtyOrdered={line.qtyOrdered}
          qtyConfirmed={line.qtyConfirmed}
          qtyLoaded={line.qtyLoaded}
          qtyReceived={line.qtyReceived}
          orderBalance={line.orderBalance}
          labels={bucketLabels}
          size="cell"
        />
      ),
    },
    {
      title: t('occ:occ.lines.columns.node'),
      key: 'node',
      width: 130,
      render: (_: unknown, line) => (
        <Tag style={{ margin: 0 }}>{nodeLabel(line.node) || renderDash()}</Tag>
      ),
    },
    {
      title: t('occ:occ.lines.columns.batches'),
      key: 'batches',
      width: 140,
      render: (_: unknown, line) => renderBatchesCell(line),
    },
    {
      title: t('occ:occ.lines.columns.owner'),
      key: 'owner',
      width: 100,
      render: (_: unknown, line) => t(`occ:occ.owner.${line.owner.role}`),
    },
    {
      title: t('occ:occ.lines.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, line) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: t('occ:occ.lines.actions.view'),
                icon: <EyeOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  openLineDrawer(line);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('occ:occ.lines.actions.actionsFor', { ref: line.sku })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  /** SO/PO 對照 columns — parents carry SO context, children the line pairs. */
  const groupedColumns: TableProps<SoPoGroupRow>['columns'] = [
    {
      title: t('occ:occ.lines.columns.order'),
      key: 'order',
      width: 260,
      render: (_: unknown, row) =>
        row.kind === 'so' ? (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span style={{ fontWeight: token.fontWeightStrong }}>{row.soNumber}</span>
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {row.customerName} ·{' '}
              {t('occ:occ.lines.grouped.lineCount', { count: row.lineCount })}
            </span>
          </div>
        ) : (
          row.line && renderProductCell(row.line)
        ),
    },
    {
      title: t('occ:occ.lines.columns.po'),
      key: 'po',
      width: 180,
      render: (_: unknown, row) => {
        if (row.kind === 'so' || !row.line) return null;
        const poRefs = row.line.poLineIds
          .map((poLineId) => docIndex.poForPoLine(poLineId))
          .filter((po): po is { poId: string; poNumber: string } => Boolean(po));
        const distinct = [...new Map(poRefs.map((po) => [po.poId, po])).values()];
        if (distinct.length === 0) {
          return (
            <span style={{ color: token.colorTextDisabled }}>
              {t('occ:occ.lines.grouped.poNone')}
            </span>
          );
        }
        return (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {distinct.map((po) => (
              <Tag
                key={po.poId}
                style={{ margin: 0, cursor: 'pointer', color: token.colorPrimary }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/auth/purchase-orders/${po.poId}`);
                }}
              >
                {po.poNumber}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t('occ:occ.lines.columns.buckets'),
      key: 'buckets',
      width: 290,
      render: (_: unknown, row) => {
        const source = row.kind === 'so' ? row : row.line;
        if (!source) return null;
        return (
          <QuantityBucketBar
            qtyOrdered={source.qtyOrdered ?? 0}
            qtyConfirmed={source.qtyConfirmed ?? 0}
            qtyLoaded={source.qtyLoaded ?? 0}
            qtyReceived={source.qtyReceived ?? 0}
            orderBalance={source.orderBalance ?? 0}
            labels={bucketLabels}
            size="cell"
          />
        );
      },
    },
    {
      title: t('occ:occ.lines.columns.node'),
      key: 'node',
      width: 130,
      render: (_: unknown, row) =>
        row.kind === 'line' && row.line ? (
          <Tag style={{ margin: 0 }}>{nodeLabel(row.line.node) || renderDash()}</Tag>
        ) : null,
    },
    {
      title: t('occ:occ.lines.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) =>
        row.kind === 'line' && row.line ? (
          <Button
            type="text"
            aria-label={t('occ:occ.lines.actions.actionsFor', { ref: row.line.sku })}
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              if (row.line) openLineDrawer(row.line);
            }}
          />
        ) : null,
    },
  ];

  const emptyState = isFiltered ? (
    <ListEmptyState
      title={t('occ:occ.states.noResults')}
      actionLabel={t('occ:occ.states.clearFilters')}
      onAction={clearAllFilters}
      actionType="default"
    />
  ) : (
    <ListEmptyState title={t('occ:occ.states.noLines')} />
  );

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="lines" />
      {/* Search + filters + the SO/PO 對照 grouping toggle */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center tw-flex-wrap">
        <Input
          placeholder={t('occ:occ.lines.searchPlaceholder')}
          value={linesFilters.search}
          onChange={(e) => setLinesFilters({ search: e.target.value })}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<LinesFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleApplyFilters}
          initialFilters={initialPopoverFilters}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        {productFilter && (
          <Tag
            closable
            onClose={clearProductFilter}
            style={{ marginInlineEnd: 0, paddingBlock: token.paddingXXS }}
          >
            {t('occ:occ.lines.columns.product')}: {productChipLabel}
          </Tag>
        )}
        <Segmented
          value={linesFilters.groupBySo ? 'grouped' : 'flat'}
          onChange={(value) => setLinesFilters({ groupBySo: value === 'grouped' })}
          options={[
            { value: 'flat', label: t('occ:occ.lines.viewFlat') },
            { value: 'grouped', label: t('occ:occ.lines.viewGrouped') },
          ]}
          size="large"
          style={{ marginLeft: 'auto' }}
        />
      </div>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        {linesFilters.groupBySo ? (
          <Table<SoPoGroupRow>
            rowKey="key"
            dataSource={groupedRows}
            columns={groupedColumns}
            loading={isLoading}
            expandable={{ defaultExpandAllRows: true }}
            onRow={(row) => ({
              onClick: () => {
                if (row.kind === 'line' && row.line) openLineDrawer(row.line);
              },
              style: row.kind === 'line' ? { cursor: 'pointer' } : undefined,
            })}
            locale={{ emptyText: emptyState }}
            pagination={false}
            scroll={{ x: 940 }}
          />
        ) : (
          <Table<OccLensLine>
            rowKey="id"
            dataSource={filteredLines}
            columns={flatColumns}
            loading={isLoading}
            onRow={(line) => ({
              onClick: () => openLineDrawer(line),
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: emptyState }}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 1130 }}
          />
        )}
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />

      <LineDrawer
        open={lineDrawerOpen}
        line={drawerLine}
        syncedAt={syncedAt}
        onClose={() => setLineDrawerOpen(false)}
        onOpenBatch={openBatchDrawer}
      />
      <BatchDrawer
        open={batchDrawerOpen}
        batchId={drawerBatchId}
        syncedAt={syncedAt}
        onClose={() => setBatchDrawerOpen(false)}
      />
    </>
  );
};

export default OrderLinesLens;
